// Stammdaten der Oberfläche (GET /api/meta): einmal nach der Anmeldung laden, überall nachschlagen.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  metaApi,
  type CustomActivityType,
  type CustomField,
  type LeadStatus,
  type Meta,
  type NutzerKurz,
} from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { organisationApi } from "../api/organisation";
import { zeitEinstellen } from "../aktivitaeten/zeiten";
import { wirksameStatusFarbe } from "../werkzeuge/statusFarben";

export interface MetaWert {
  meta: Meta | null;
  laedt: boolean;
  fehler: unknown;
  neuLaden: () => Promise<void>;
  status: (id: string | null | undefined) => LeadStatus | undefined;
  nutzer: (id: string | null | undefined) => NutzerKurz | undefined;
  nutzerName: (id: string | null | undefined) => string;
  feld: (id: string) => CustomField | undefined;
  typ: (id: string | null | undefined) => CustomActivityType | undefined;
  /** Aktive Nutzer zuerst, alphabetisch. */
  nutzerListe: NutzerKurz[];
}

const MetaKontext = createContext<MetaWert | null>(null);

export function MetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);

  const neuLaden = useCallback(async () => {
    try {
      const m = await metaApi.alles();
      // Status-Farben nach Name (statusFarben.ts): Listen, Kopf, Verlauf und Auswahlen lesen alle `color` aus den Stammdaten
      // Lead-Feld „Follow-Up Score“ heißt in der Oberfläche „Lead Score“ (Dilsar, 18.10.2026). Nur Anzeigename: ID und Close-Name bleiben,
      // damit Abgleich und Automatik passen. Gibt es schon ein eigenes Lead-Feld „Lead Score“, wird nicht umbenannt.
      const leadFelder = m.custom_fields.lead ?? [];
      const hatEigenesScoreFeld = leadFelder.some((f) => f.name.trim().toLowerCase() === "lead score");
      const customFelder = hatEigenesScoreFeld
        ? m.custom_fields
        : {
            ...m.custom_fields,
            lead: leadFelder.map((f) => (f.name.trim().toLowerCase() === "follow-up score" ? { ...f, name: "Lead Score" } : f)),
          };
      setMeta({
        ...m,
        custom_fields: customFelder,
        lead_statuses: m.lead_statuses.map((s) => ({ ...s, color: wirksameStatusFarbe(s.label, s.color) ?? s.color })),
      });
      setFehler(null);
      // Zeitzone und Wochenbeginn der Organisation für die Zeiträume der Listen (Lesen darf scheitern, dann gilt der Standard)
      organisationApi
        .organisation()
        .then((r) => zeitEinstellen({ zeitzone: r.organisation.zeitzone, wochenbeginn: r.organisation.wochenbeginn }))
        .catch(() => undefined);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    void neuLaden();
  }, [neuLaden]);

  // Abgleich meldet neue Konfiguration (z. B. neuer Custom-Activity-Typ): still neu laden.
  useEchtzeit(["sync.status"], (e) => {
    const key = String((e.data as { key?: string }).key ?? "");
    if (key && key !== "leads" && key !== "activities" && key !== "tasks") void neuLaden();
  });

  // Verwaltung (Typen, Felder, Status, Pipelines, Links, Organisation) meldet `meta.updated`: auch andere Fenster laden still neu.
  useEchtzeit(["meta.updated"], () => void neuLaden());

  const wert = useMemo<MetaWert>(() => {
    const status = new Map<string, LeadStatus>();
    const nutzer = new Map<string, NutzerKurz>();
    const felder = new Map<string, CustomField>();
    const typen = new Map<string, CustomActivityType>();
    if (meta) {
      meta.lead_statuses.forEach((s) => status.set(s.id, s));
      meta.users.forEach((u) => {
        nutzer.set(u.id, u);
        if (u.close_user_id) nutzer.set(u.close_user_id, u);
      });
      for (const liste of Object.values(meta.custom_fields)) liste.forEach((f) => felder.set(f.id, f));
      meta.custom_activity_types.forEach((t) => typen.set(t.id, t));
    }
    const nutzerListe = [...(meta?.users ?? [])].sort(
      (a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name, "de"),
    );
    return {
      meta,
      laedt,
      fehler,
      neuLaden,
      status: (id) => (id ? status.get(id) : undefined),
      nutzer: (id) => (id ? nutzer.get(id) : undefined),
      nutzerName: (id) => (id ? nutzer.get(id)?.name ?? "Unbekannter Nutzer" : ""),
      feld: (id) => felder.get(id.replace(/^custom\./, "")),
      typ: (id) => (id ? typen.get(id) : undefined),
      nutzerListe,
    };
  }, [meta, laedt, fehler, neuLaden]);

  return <MetaKontext.Provider value={wert}>{children}</MetaKontext.Provider>;
}

export function useMeta(): MetaWert {
  const wert = useContext(MetaKontext);
  if (!wert) throw new Error("useMeta außerhalb von MetaProvider");
  return wert;
}
